import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Settings, Camera, MapPin, Clock, Shield, Users, Save, RotateCcw,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const REGIONS = [
  { id: "GJ", label: "Gujarat", color: "#C0272D" },
  { id: "RJ", label: "Rajasthan", color: "#F5A623" },
];

const DEFAULT_SETTINGS = {
  photo_punch_in: false,
  photo_punch_out: false,
  auto_checkin: false,
  auto_checkout: false,
  client_checkin: true,
  client_checkout: true,
  geofence_radius_meters: 200,
  max_daily_visits: 20,
  min_visit_duration_mins: 5,
  require_gps_always: true,
  allow_manual_attendance: false,
  working_hours_start: "09:00",
  working_hours_end: "18:00",
};

const SettingRow = ({ icon: Icon, label, description, children }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`setting-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3 flex-1">
      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="shrink-0 ml-4">{children}</div>
  </div>
);

const FieldForceSettingsPage = () => {
  const { user } = useAuth();
  const [activeRegion, setActiveRegion] = useState("GJ");
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { fetchSettings(activeRegion); }, [activeRegion]);

  const fetchSettings = async (regionId) => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/field-force/settings/${regionId}`);
      const data = await res.json();
      if (data.success && data.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...data.data });
      } else {
        setSettings({ ...DEFAULT_SETTINGS });
      }
      setDirty(false);
    } catch {
      setSettings({ ...DEFAULT_SETTINGS });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/field-force/settings/${activeRegion}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Settings saved for ${activeRegion === "GJ" ? "Gujarat" : "Rajasthan"}`);
        setDirty(false);
      } else {
        toast.error("Failed to save settings");
      }
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...DEFAULT_SETTINGS });
    setDirty(true);
  };

  return (
    <div className="space-y-6" data-testid="ff-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Field Force Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Configure field force rules per region</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={!dirty} data-testid="btn-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button className="btn-primary" onClick={handleSave} disabled={!dirty || saving} data-testid="btn-save-settings">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      {/* Region Tabs */}
      <Tabs value={activeRegion} onValueChange={setActiveRegion}>
        <TabsList className="bg-slate-100">
          {REGIONS.map((r) => (
            <TabsTrigger key={r.id} value={r.id} data-testid={`tab-${r.id}`}>
              <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: r.color }} />
              {r.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {dirty && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700">
          You have unsaved changes for {activeRegion === "GJ" ? "Gujarat" : "Rajasthan"}.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-6 h-6 spinner"></div></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Attendance & Photos */}
          <Card className="p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#8B1A1A]" /> Attendance & Photos
            </h3>
            <div className="space-y-0">
              <SettingRow icon={Camera} label="Photo on Punch In" description="Require selfie photo when punching in">
                <Switch checked={settings.photo_punch_in} onCheckedChange={(v) => updateSetting("photo_punch_in", v)} data-testid="switch-photo-in" />
              </SettingRow>
              <SettingRow icon={Camera} label="Photo on Punch Out" description="Require selfie photo when punching out">
                <Switch checked={settings.photo_punch_out} onCheckedChange={(v) => updateSetting("photo_punch_out", v)} data-testid="switch-photo-out" />
              </SettingRow>
              <SettingRow icon={Clock} label="Auto Check-In" description="Automatically check in when entering office geofence">
                <Switch checked={settings.auto_checkin} onCheckedChange={(v) => updateSetting("auto_checkin", v)} data-testid="switch-auto-checkin" />
              </SettingRow>
              <SettingRow icon={Clock} label="Auto Check-Out" description="Automatically check out when leaving geofence at end of day">
                <Switch checked={settings.auto_checkout} onCheckedChange={(v) => updateSetting("auto_checkout", v)} data-testid="switch-auto-checkout" />
              </SettingRow>
              <SettingRow icon={Shield} label="Allow Manual Attendance" description="Allow employees to mark attendance without GPS">
                <Switch checked={settings.allow_manual_attendance} onCheckedChange={(v) => updateSetting("allow_manual_attendance", v)} data-testid="switch-manual-att" />
              </SettingRow>
            </div>
          </Card>

          {/* Client Visits */}
          <Card className="p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#8B1A1A]" /> Client Visit Rules
            </h3>
            <div className="space-y-0">
              <SettingRow icon={MapPin} label="Client Check-In" description="Enable check-in at client/dealer locations">
                <Switch checked={settings.client_checkin} onCheckedChange={(v) => updateSetting("client_checkin", v)} data-testid="switch-client-checkin" />
              </SettingRow>
              <SettingRow icon={MapPin} label="Client Check-Out" description="Enable check-out at client/dealer locations">
                <Switch checked={settings.client_checkout} onCheckedChange={(v) => updateSetting("client_checkout", v)} data-testid="switch-client-checkout" />
              </SettingRow>
              <SettingRow icon={MapPin} label="Geofence Radius (meters)" description="Radius for auto check-in/out at client locations">
                <Input type="number" className="w-24 h-8 text-sm" value={settings.geofence_radius_meters}
                  onChange={(e) => updateSetting("geofence_radius_meters", parseInt(e.target.value) || 200)}
                  data-testid="input-geofence" />
              </SettingRow>
              <SettingRow icon={Users} label="Max Daily Visits" description="Maximum client visits allowed per day">
                <Input type="number" className="w-24 h-8 text-sm" value={settings.max_daily_visits}
                  onChange={(e) => updateSetting("max_daily_visits", parseInt(e.target.value) || 20)}
                  data-testid="input-max-visits" />
              </SettingRow>
              <SettingRow icon={Clock} label="Min Visit Duration (mins)" description="Minimum time at each client location">
                <Input type="number" className="w-24 h-8 text-sm" value={settings.min_visit_duration_mins}
                  onChange={(e) => updateSetting("min_visit_duration_mins", parseInt(e.target.value) || 5)}
                  data-testid="input-min-duration" />
              </SettingRow>
            </div>
          </Card>

          {/* GPS & Working Hours */}
          <Card className="p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#8B1A1A]" /> GPS & Working Hours
            </h3>
            <div className="space-y-0">
              <SettingRow icon={MapPin} label="Require GPS Always" description="GPS must remain enabled during working hours">
                <Switch checked={settings.require_gps_always} onCheckedChange={(v) => updateSetting("require_gps_always", v)} data-testid="switch-gps-always" />
              </SettingRow>
              <SettingRow icon={Clock} label="Working Hours Start" description="Start of working day">
                <Input type="time" className="w-28 h-8 text-sm" value={settings.working_hours_start}
                  onChange={(e) => updateSetting("working_hours_start", e.target.value)}
                  data-testid="input-hours-start" />
              </SettingRow>
              <SettingRow icon={Clock} label="Working Hours End" description="End of working day">
                <Input type="time" className="w-28 h-8 text-sm" value={settings.working_hours_end}
                  onChange={(e) => updateSetting("working_hours_end", e.target.value)}
                  data-testid="input-hours-end" />
              </SettingRow>
            </div>
          </Card>

          {/* Region Info */}
          <Card className="p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#8B1A1A]" /> Region Info
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Region</span>
                <Badge style={{ backgroundColor: REGIONS.find(r => r.id === activeRegion)?.color + "20", color: REGIONS.find(r => r.id === activeRegion)?.color }}>
                  {activeRegion === "GJ" ? "Gujarat" : "Rajasthan"}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Last Updated</span>
                <span className="text-slate-800 font-medium">{settings.updated_at ? new Date(settings.updated_at).toLocaleDateString("en-IN") : "Never"}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-md text-xs text-slate-600 mt-3">
                These settings apply to all field force employees in the {activeRegion === "GJ" ? "Gujarat" : "Rajasthan"} region. Changes take effect immediately.
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FieldForceSettingsPage;
